import { useCustomForm } from "@/hooks/useCustomForm"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  IconButton,
  Autocomplete,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import ClearIcon from "@mui/icons-material/Clear"
import { z } from "zod"
import SingleSelect from "@/components/Select/SingleSelect"
import { toast } from "sonner"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import React, { useEffect, useState } from "react"
import { useCrudContext } from "@/context/crudContext"
import LetterAvatar from "@/components/Avatar/LetterAvatar"
import AddIcon from "@mui/icons-material/Add"
import AddContactModal from "../customers/Model"
import Grid from "@mui/material/Grid2"
import ProductableAdjustments from "@/components/Products/ProductableAdjustments"
import SecondaryButton from "@/components/Buttons/SecondaryButton"
import PrimaryButton from "@/components/Buttons/PrimaryButton"
import { DevTool } from "@/components/FormDevTool"
import { Textarea } from "@mantine/core"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { useNow } from "@mui/x-date-pickers/internals"
import dayjs from "dayjs"
import { useSalesReturnCrudContext } from "@/context/salesReturnCrudContext"
import OrderSelectComponent from "@/components/Orders/OrderSelectComponent"
import { apiFetch } from "@/hooks/apiFetch"
import SalesReturnProductable from "./SalesReturnProductable"

const StockAdjustmentSchema = z.object({
  order_id: z.number(),
  notes: z.string().nullable(),
  date: z
    .any() // Accept any input (dayjs object or string)
    .transform((value) => {
      // Convert to Date object at end of day
      const dayjsObj = dayjs.isDayjs(value) ? value : dayjs(value, "YYYY-MM-DD")
      return dayjsObj.isValid() ? dayjsObj.endOf("day").toDate() : null
    })
    .refine((value) => value !== null, {
      message: "Invalid date format",
    }),
})

const AddStockAdjustmentModal = ({ open, onClose }) => {
  const queryClient = useQueryClient()
  const {
    openSalesReturnDialog,
    setOpenSalesReturnDialog,
    selectedsalesReturnRow,
    setSelectedSalesReturnRow,
  } = useSalesReturnCrudContext()

  const {
    api,
    handleSubmit,
    errors,
    isSubmitting,
    reset,
    register,
    setValue,
    control,
    Controller,
    getValues,
  } = useCustomForm({
    resolver: zodResolver(StockAdjustmentSchema),
    defaultValues: {
      date: dayjs(),
    },
  })

  const defaultOnClose = () => {
    setOpenSalesReturnDialog(false)
    setSelectedSalesReturnRow(null)
  }

  useEffect(() => {
    if (selectedsalesReturnRow) {
      Object.entries(selectedsalesReturnRow).forEach(([key, value]) => {
        if (typeof value === "object" && value !== null) {
          Object.entries(value).forEach(([nestedKey, nestedValue]) => {
            setValue(`${key}.${nestedKey}`, nestedValue)
          })
        } else {
          setValue(`${key}`, value)
        }
      })
    } else {
      reset()
    }
  }, [selectedsalesReturnRow, setValue, reset])

  const [selectedOrder, setSelectedOrder] = useState(null)
  const onOrderChange = async (orderId) => {
    if (!orderId) return

    queryClient
      .fetchQuery({
        queryKey: ["order", orderId],
        queryFn: async () => await apiFetch(`order/${orderId}`),
      })
      .then((data) => {
        setSelectedOrder(data.data)
      })
      .catch((err) => {
        console.error("Error fetching order:", err)
      })
  }

  const onSubmit = async (data) => {
    const method = selectedsalesReturnRow ? "PUT" : "POST"
    const url = selectedsalesReturnRow
      ? `sales-return/${selectedsalesReturnRow.id}`
      : "sales-return"

    const response = await api(url, {
      method,
      body: JSON.stringify({ ...data, products: getValues("products") }),
    })

    if (response) {
      queryClient.invalidateQueries({ queryKey: ["sales-return"] })
      queryClient.invalidateQueries({ queryKey: ["order"] })
      queryClient.invalidateQueries({ queryKey: ["order-detail"] })
      queryClient.invalidateQueries({ queryKey: ["website-orders"] })
      toast.success(
        `Sales Return successfully ${selectedsalesReturnRow ? "updated" : "added"}`
      )
      onClose ? onClose() : defaultOnClose()
      reset()
    }
  }

  return (
    <>
      <Modal
        open={open ? open : openSalesReturnDialog}
        onClose={defaultOnClose}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            margin: "100px auto",
            bgcolor: "white",
            width: "80vw",
            maxHeight: "90vh",
            overflowY: "auto",
            bRadius: 1,
            boxShadow: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bRadius: 1,
              background: "#f6f7f9",
              padding: { xs: "15px 20px", md: "25px" },
            }}
          >
            <Typography
              id="modal-modal-title"
              variant="h6"
              sx={{
                fontWeight: "600",
                fontSize: { xs: "16px", md: "18px" },
              }}
              className="text-black"
            >
              {selectedsalesReturnRow
                ? "Update Sales Return"
                : "Add New Sales Return"}
            </Typography>
            <IconButton
              aria-label="remove"
              size="small"
              onClick={onClose ? onClose : defaultOnClose}
            >
              <ClearIcon />
            </IconButton>
          </Box>

          <Box
            sx={{ padding: 2 }}
            component="form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 8 }}>
                <Controller
                  name="order_id"
                  control={control}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <OrderSelectComponent
                      value={value}
                      onChange={(val) => {
                        onChange(val)
                        onOrderChange(val)
                      }}
                      isError={!!error}
                      errorMessage={error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Controller
                    name="date"
                    control={control}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <DatePicker
                        label="Date"
                        value={value ? dayjs(value) : null}
                        onChange={(newValue) => onChange(newValue)} // <-- Don't format to string
                        sx={{
                          width: "100%",
                          "& fieldset": {
                            border: "1px solid #D5D9E2",
                            borderRadius: "7px",
                          },
                        }}
                        maxDate={dayjs()}
                        slotProps={{
                          textField: {
                            error: !!error,
                            helperText: error?.message,
                          },
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>

            <Grid size={12}>
              <SalesReturnProductable
                label="Products"
                name="products"
                controller={Controller}
                setValue={setValue}
                control={control}
                errors={errors}
                options={selectedOrder?.order_items || []}
                helperText={errors.products?.message}
                onClose={onClose}
                isSubmitting={isSubmitting}
              />
            </Grid>

            <Grid size={12}>
              <Textarea
                placeholder="Note"
                error={!!errors.notes}
                helperText={errors.notes?.message}
                {...register("notes")}
              />
            </Grid>
            <Grid size={12} mt={1}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "end",
                  gap: "10px",
                }}
              >
                <SecondaryButton label="Cancel" onClick={defaultOnClose} />
                <PrimaryButton
                  type="submit"
                  label="Create"
                  isSubmitting={isSubmitting}
                />
              </Box>
            </Grid>

            <DevTool control={control} />
          </Box>
        </Box>
      </Modal>
    </>
  )
}

export default AddStockAdjustmentModal
